import type { IconName } from '@/ui/icons';
/**
 * An activity's icon: the library's own choice when one is set (shift_tasks.icon), otherwise from its name (V19 day plan, gap T2, 2026-10-18). The library stores no icon
 * (shift_tasks has id, name, description), so the web app reads the name, as V19's
 * ACTIVITY_CATALOG_V13 did, and anything it does not know gets the clipboard. The first rule that
 * matches wins: «Tell varelager» is counting, not stock, and «Stengerutine» is closing.
 */
const RULES: [RegExp, IconName][] = [
  [/lunsj|pause|middag|frokost|\bmat\b/, 'utensils'],
  [/åpn/, 'door'],
  [/steng|lukk|låse/, 'lock'],
  [/tell|telling|opptell|inventar/, 'clipboard'],
  [/kasse|kassa|betal/, 'store'],
  // before «lever»: an overlevering is a handover, not a delivery
  [/overlever|beskjed|melding/, 'chat'],
  [/lever|mottak|pakke|post\b/, 'truck'],
  [/påfyll|vare|lager|bestill|hylle/, 'box'],
  [/kjøl|frys|temperatur/, 'thermometer'],
  [/rengj|vask|rydd|støv|søppel/, 'sparkle'],
  [/kunde|service|opplær|kurs|møte/, 'users'],
  [/admin|kontor|rapport|papir|regnskap/, 'document'],
  [/telefon|ring/, 'phone'],
  [/e-?post|\bmail/, 'mail'],
];

/**
 * The icons a manager may choose for an activity (owner 2026-10-18). The same list as the backend's
 * allowlist (backend/services/activityIcons.js), which refuses any other name.
 */
export const ICONS: IconName[] = [
  'clipboard',
  'utensils',
  'door',
  'lock',
  'store',
  'box',
  'truck',
  'thermometer',
  'sparkle',
  'users',
  'user',
  'chat',
  'document',
  'phone',
  'mail',
  'calendar',
  'clock',
  'check',
  'briefcase',
  'heart',
  'banknote',
  'pin',
  'folder',
  'sun',
];
const chosen = (v?: string | null): v is IconName => !!v && (ICONS as string[]).includes(v);

/** The icon the manager chose in the library, or else the one the name suggests. */
export function activityIcon(name: string, lunch = false, override?: string | null): IconName {
  if (lunch) return 'utensils';
  if (chosen(override)) return override;
  const n = name.toLowerCase();
  return RULES.find(([re]) => re.test(n))?.[1] ?? 'clipboard';
}

/**
 * An activity's colour (#202): the design's tones, the same list as the backend's allowlist
 * (backend/services/activityIcons.js COLORS). Null is blue, as every activity was before; a one-off is slate.
 */
export const COLORS = ['blue', 'teal', 'violet', 'plum', 'cyan', 'slate', 'indigo'] as const;
export type ActivityColor = (typeof COLORS)[number];
export const colorOf = (c?: string | null, oneOff = false): ActivityColor =>
  oneOff ? 'slate' : (COLORS as readonly string[]).includes(c ?? '') ? (c as ActivityColor) : 'blue';
/** The tone's three tokens as the variables a block, a bar or a tile reads (--c-f fill, --c-l line, --c-i ink). */
export const toneOf = (c?: string | null, oneOff = false): Record<string, string> => {
  const k = colorOf(c, oneOff);
  return { '--c-f': `var(--${k}-f)`, '--c-l': `var(--${k}-l)`, '--c-i': `var(--${k}-i)` };
};
